import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { languages } from "locales/languages"
import { PATCH_PAGE } from "core/constants"
import { getDermatoSubReasonValue } from "./dermato_models"
import { PATH } from "./models"
import { getPatchTargetId } from "./patch_models"
import { Answer, Question, Value } from "./types"
import { findQuestionById } from "./utils"
import styles from "./DermatoRecap.module.scss"
import { UploadDocStore } from "types/store"

export const DERMATO_RECAP_STEP_COUNT = 2

type RecapField =
  | { type: "reason"; label: string }
  | { type: "subReason"; label: string }
  | { type: "question"; questionId: string; label: string }

const QUESTIONS_LINE_PARENT_IDS = [
  PATH.anamnesis_questions,
  PATH.dermato_anamnesis_questions,
  PATH.dermato_questions,
]

const RECAP_STEP_FIELDS: RecapField[][] = [
  [
    { type: "reason", label: languages.dermatoRecapMotif },
    { type: "subReason", label: languages.dermatoRecapSubReason },
    {
      type: "question",
      questionId: PATH.dermato_body_zone,
      label: languages.dermatoRecapBodyZones,
    },
    {
      type: "question",
      questionId: PATH.dermato_symptoms,
      label: languages.dermatoRecapSymptoms,
    },
    {
      type: "question",
      questionId: PATH.illness,
      label: languages.dermatoRecapChronicIllness,
    },
    {
      type: "question",
      questionId: PATH.operation,
      label: languages.dermatoRecapOperations,
    },
    {
      type: "question",
      questionId: PATH.allergy,
      label: languages.dermatoRecapAllergies,
    },
    {
      type: "question",
      questionId: PATH.drug,
      label: languages.dermatoRecapMedications,
    },
  ],
  [
    {
      type: "question",
      questionId: PATH.dermato_history,
      label: languages.dermatoRecapDermHistory,
    },
    {
      type: "question",
      questionId: PATH.dermato_family_cancer,
      label: languages.dermatoRecapFamilyCancer,
    },
    {
      type: "question",
      questionId: PATH.dermato_recent_travel,
      label: languages.dermatoRecapRecentTravel,
    },
    {
      type: "question",
      questionId: PATH.dermato_zone_evolution,
      label: languages.dermatoRecapZoneEvolution,
    },
    {
      type: "question",
      questionId: PATH.dermato_problem_duration,
      label: languages.dermatoRecapProblemDuration,
    },
    {
      type: "question",
      questionId: PATH.dermato_zone_evolution_duration,
      label: languages.dermatoRecapZoneEvolutionDuration,
    },
    {
      type: "question",
      questionId: PATH.dermato_last_control,
      label: languages.dermatoRecapLastControl,
    },
    {
      type: "question",
      questionId: PATH.dermato_sun_exposure,
      label: languages.dermatoRecapSunExposure,
    },
    {
      type: "question",
      questionId: PATH.dermato_sunburn_before_20,
      label: languages.dermatoRecapSunburnBefore20,
    },
    {
      type: "question",
      questionId: PATH.dermato_phototype,
      label: languages.dermatoRecapPhototype,
    },
  ],
]

const isLineAnswer = (
  value: Value,
): value is { title: string; value: Value } =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  "title" in value &&
  "value" in value

const formatValue = (value: Value): string => {
  if (isLineAnswer(value)) {
    const inner = value.value
    if (typeof inner === "boolean") return inner ? languages.yes : languages.no
    if (inner && typeof inner === "object" && "label" in inner) {
      return String((inner as { label: string }).label)
    }
    return String(inner)
  }
  if (value && typeof value === "object" && "label" in value) {
    return String((value as { label: string }).label)
  }
  if (typeof value === "boolean") return value ? languages.yes : languages.no
  return String(value)
}

const hasOwnLabel = (value: Value): boolean =>
  value !== null && typeof value === "object" && "label" in value

const findReplyTitle = (
  questionId: string,
  value: Value,
  questions: Question[],
): string | undefined =>
  findQuestionById(questionId, questions)?.replies.find(
    (reply) => reply.value === value,
  )?.title

const formatAnswer = (
  answer: Answer,
  questions: Question[],
  questionId = answer.questionId,
): string => {
  if (answer.value.some(isLineAnswer)) {
    return answer.value
      .filter(isLineAnswer)
      .map((entry) => formatValue(entry))
      .join(", ")
  }

  return answer.value
    .map((value) =>
      // Le titre d'une réponse peut être du HTML destiné au carrousel (phototype) :
      // une valeur qui porte son propre libellé se décrit toujours mieux elle-même.
      hasOwnLabel(value)
        ? formatValue(value)
        : findReplyTitle(questionId, value, questions) ?? formatValue(value),
    )
    .join(", ")
}

const findLineAnswerValue = (
  answers: Answer[],
  questionId: string,
): Value | undefined => {
  for (const parentId of QUESTIONS_LINE_PARENT_IDS) {
    const parentAnswer = answers.find((entry) => entry.questionId === parentId)
    const lineAnswer = parentAnswer?.value.find(
      (value) => isLineAnswer(value) && value.title === questionId,
    )
    if (lineAnswer && isLineAnswer(lineAnswer)) {
      return lineAnswer.value
    }
  }
  return undefined
}

const isDetailAnswer = (values: Value[]): boolean => {
  if (values.length === 0) return false
  if (values.length === 1 && typeof values[0] === "boolean") return false
  return values.some((value) => !isLineAnswer(value))
}

const getRecapAnswer = (
  answers: Answer[],
  questionId: string,
): Answer | undefined => {
  const directAnswer = answers.find((entry) => entry.questionId === questionId)
  const lineValue = findLineAnswerValue(answers, questionId)

  if (directAnswer?.value.length && isDetailAnswer(directAnswer.value)) {
    return directAnswer
  }

  if (lineValue !== undefined) {
    return { questionId, value: [lineValue] }
  }

  if (directAnswer?.value.length) {
    return directAnswer
  }

  return undefined
}

const formatReasonValue = (value: Value, questions: Question[]): string =>
  findReplyTitle(PATH.reason, value, questions) ?? formatValue(value)

const formatSubReasonValue = (answers: Answer[]): string => {
  const label = getDermatoSubReasonValue(answers)
  return label != null ? String(label) : ""
}

export const buildDermatoRecapStepItems = (
  answers: Answer[],
  questions: Question[],
  step: number,
): { label: string; value: string; questionId: string }[] => {
  const fields = RECAP_STEP_FIELDS[step]
  if (!fields) return []

  return fields.flatMap((field) => {
    if (field.type === "reason") {
      const answer = answers.find((entry) => entry.questionId === PATH.reason)
      if (!answer?.value.length) return []
      return [
        {
          label: field.label,
          value: formatReasonValue(answer.value[0], questions),
          questionId: PATH.reason,
        },
      ]
    }

    if (field.type === "subReason") {
      const value = formatSubReasonValue(answers)
      if (!value) return []
      // Le motif détaillé se modifie depuis le motif : c'est lui qui décide
      // de la sous-question affichée.
      return [{ label: field.label, value, questionId: PATH.reason }]
    }

    // La durée d'évolution de la zone n'est pertinente que si le patient a
    // déclaré une évolution récente de la zone (réponse résiduelle possible
    // si l'utilisateur est revenu en arrière).
    if (
      field.questionId === PATH.dermato_zone_evolution_duration &&
      findLineAnswerValue(answers, PATH.dermato_zone_evolution) !== true
    ) {
      return []
    }

    const answer = getRecapAnswer(answers, field.questionId)
    if (!answer) return []

    return [
      {
        label: field.label,
        value: formatAnswer(answer, questions, field.questionId),
        questionId: getPatchTargetId(field.questionId),
      },
    ]
  })
}

// Crayon de la maquette (Figma KIOSK-843).
const EditIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 28 28"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <path
      d="M25.8485 24.7913V26.5413H2.15625V24.7913H25.8485ZM24.6852 6.49951L11.2196 20.708H6.01969V14.4679L6.27946 14.2104L19.5503 1.08203L24.6852 6.49951Z"
      fill="currentColor"
    />
  </svg>
)

export type DermatoRecapHandle = {
  goBack: () => boolean
  goNext: () => boolean
}

export const DermatoRecap = forwardRef<
  DermatoRecapHandle,
  {
    answers: Answer[]
    questions: Question[]
    capturedPhotos?: string[]
    initialStep?: number
    initialEditableSteps?: number[]
  }
>((
  {
    answers,
    questions,
    capturedPhotos = [],
    initialStep = 0,
    initialEditableSteps,
  },
  ref,
) => {
  const [step, setStep] = useState(initialStep)
  // Une page de résumé n'affiche ses crayons qu'après un clic sur la section :
  // état à sens unique, conservé quand on revient d'une modification.
  const [editableSteps, setEditableSteps] = useState<number[]>(
    initialEditableSteps ?? [],
  )
  const navigate = useNavigate()

  const dermatoPatientDetails = useSelector(
    ({ upload }: { upload: UploadDocStore }) => upload.dermatoPatientDetails,
  )
  const dermatoPatientDetailsReceivedAt = useSelector(
    ({ upload }: { upload: UploadDocStore }) => upload.dermatoPatientDetailsReceivedAt,
  )
  // Recover patient_dermato_details found within the last 15 minutes
  const isDetailsRecent =
    dermatoPatientDetailsReceivedAt != null &&
    Date.now() - dermatoPatientDetailsReceivedAt < 900_000
  const hasDetailsStep = Boolean(dermatoPatientDetails?.trim()) && isDetailsRecent
  const detailsStepOffset = hasDetailsStep ? 1 : 0

  const isDetailsStep = hasDetailsStep && step === DERMATO_RECAP_STEP_COUNT
  const isPhotoStep = step >= DERMATO_RECAP_STEP_COUNT + detailsStepOffset
  const photoIndex = step - DERMATO_RECAP_STEP_COUNT - detailsStepOffset
  const photoSrc = capturedPhotos[photoIndex]

  const totalSteps =
    DERMATO_RECAP_STEP_COUNT + detailsStepOffset + capturedPhotos.length

  const items = useMemo(() => {
    if (step >= DERMATO_RECAP_STEP_COUNT) return []
    return buildDermatoRecapStepItems(answers, questions, step)
  }, [answers, questions, step])

  useImperativeHandle(ref, () => ({
    goBack: () => {
      if (step > 0) {
        setStep((current) => current - 1)
        return true
      }
      return false
    },
    goNext: () => {
      if (step < totalSteps - 1) {
        setStep((current) => current + 1)
        return true
      }
      return false
    },
  }))

  if (isDetailsStep) {
    return (
      <div className={styles.Recap}>
        <div className={styles.RecapItem}>
          <span className={styles.RecapLabel}>
            {languages.dermatoRecapPatientDetails} :{" "}
          </span>
          <span className={styles.RecapValue}>
            {dermatoPatientDetails ?? "—"}
          </span>
        </div>
      </div>
    )
  }

  if (isPhotoStep) {
    return (
      <div className={styles.Recap}>
        <div className={styles.PhotoStep}>
          <span className={styles.PhotoLabel}>Photo #{photoIndex + 1}</span>
          {photoSrc?.startsWith("blob:") ? (
            <iframe
              src={`${photoSrc}#toolbar=0&view=Fit`}
              title={`Photo ${photoIndex + 1}`}
              className={styles.PhotoImage}
              style={{ height: "480px", border: "none" }}
            />
          ) : (
            <img
              src={photoSrc}
              alt={`Photo ${photoIndex + 1}`}
              className={styles.PhotoImage}
            />
          )}
        </div>
      </div>
    )
  }

  const isEditable = editableSteps.includes(step)

  return (
    <div className={styles.Recap}>
      {items.map((item, index) => (
        <button
          type="button"
          key={item.label}
          className={styles.RecapItem}
          aria-label={
            isEditable
              ? `${languages.modify} : ${item.label}`
              : languages.modifyDatas
          }
          onClick={() => {
            if (!isEditable) {
              setEditableSteps((steps) => [...steps, step])
              return
            }
            navigate(`${PATCH_PAGE}/${encodeURIComponent(item.questionId)}`, {
              state: { step, editableSteps },
            })
          }}
        >
          <span className={styles.RecapLabel}>{item.label} : </span>
          <span className={styles.RecapValue}>
            {item.value}
            {isEditable && <EditIcon className={styles.RecapEditInline} />}
          </span>
          {!isEditable && index === 0 && (
            <EditIcon className={styles.RecapEditCorner} />
          )}
        </button>
      ))}
    </div>
  )
})

DermatoRecap.displayName = "DermatoRecap"
